import React, { useState, useEffect, useCallback } from 'react';
import {
  CogIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  GlobeAltIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import {
  CogIcon as CogIconSolid
} from '@heroicons/react/24/solid';

import axiosInstance from '../api/axiosInstance';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import WorkspaceSearchSettings from './WorkspaceSearchSettings';

// Design System Components
import Button from '../ui/Button';
import InputComponent, { Textarea } from '../ui/Input';
import Card, { CardHeader } from '../ui/Card';
import { StatusBadge } from '../ui/Table';
import logger from '../../utils/logger';

const ModernSettings = ({ showNotification }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const [saving, setSaving] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // Company Settings State
  const [companySettings, setCompanySettings] = useState({
    businessName: '',
    industry: '',
    businessType: 'corporation',
    website: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    }
  });

  // Regional Settings State
  const [regionalSettings, setRegionalSettings] = useState({
    language: 'en',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    currency: 'USD',
    numberFormat: 'US'
  });

  // User Management Settings State
  const [userSettings, setUserSettings] = useState({
    allowSelfRegistration: false,
    defaultUserRole: 'user',
    passwordPolicy: {
      minLength: 8,
      requireUppercase: true,
      requireNumbers: true,
      requireSpecialChars: false
    },
    sessionTimeout: 60,
    maxFailedAttempts: 5
  });

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    systemAlerts: true,
    securityAlerts: true,
    marketingEmails: false,
    weeklyReports: true,
    invoiceReminders: true
  });

  // Security Settings State
  const [securitySettings, setSecuritySettings] = useState({
    passwordExpiry: 90,
    ipWhitelist: '',
    activityLog: true,
    organizationName: '',
    organizationId: '',
    dataRetention: 365,
    backupFrequency: 'daily'
  });

  // Domains State
  const [domains, setDomains] = useState([]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch business profile, settings, and domains
      const [businessProfile, settings, domainsResponse] = await Promise.all([
        axiosInstance.get('/api/business/profile/summary'),
        axiosInstance.get('/api/business/settings').catch(() => ({ data: { success: false } })),
        axiosInstance.get('/api/business/domains').catch(() => ({ data: { success: false, domains: [] } }))
      ]);

      // Update company settings from business profile
      if (businessProfile.data.success && businessProfile.data.data) {
        const data = businessProfile.data.data;
        setCompanySettings(prev => ({
          ...prev,
          businessName: data.businessName || '',
          industry: data.industry || '',
          website: data.website || '',
          phone: data.phone || '',
          address: {
            street: data.address?.street || '',
            city: data.address?.city || '',
            state: data.address?.state || '',
            zipCode: data.address?.zipCode || '',
            country: data.address?.country || 'United States'
          }
        }));
      }

      // Update other settings if available
      if (settings.data.success && settings.data.settings) {
        const { company, regional, notifications, security } = settings.data.settings;

        // Update company settings from settings endpoint (overrides profile data)
        if (company) {
          setCompanySettings(prev => ({
            ...prev,
            businessName: company.businessName || prev.businessName,
            industry: company.industry || prev.industry,
            companySize: company.companySize || prev.companySize,
            registrationNumber: company.registrationNumber || '',
            taxId: company.taxId || '',
            website: company.website || prev.website,
            phone: company.phone || prev.phone,
            address: {
              street: company.address || prev.address?.street || '',
              city: company.city || prev.address?.city || '',
              state: company.state || prev.address?.state || '',
              zipCode: company.zipCode || prev.address?.zipCode || '',
              country: company.country || prev.address?.country || 'United States'
            }
          }));
        }

        if (regional) setRegionalSettings(prev => ({ ...prev, ...regional }));
        if (notifications) setNotificationSettings(prev => ({ ...prev, ...notifications }));
        if (security) setSecuritySettings(prev => ({ ...prev, ...security }));
      }

      // Update domains
      if (domainsResponse.data.success && domainsResponse.data.domains) {
        setDomains(domainsResponse.data.domains);
      }

    } catch (error) {
      logger.error('Error fetching settings:', error);
      showNotification && showNotification('Failed to load some settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save all settings in one request
      const settingsResponse = await axiosInstance.post('/api/business/settings', {
        company: {
          businessName: companySettings.businessName,
          industry: companySettings.industry,
          companySize: companySettings.companySize,
          registrationNumber: companySettings.registrationNumber,
          taxId: companySettings.taxId,
          address: companySettings.address?.street,
          city: companySettings.address?.city,
          state: companySettings.address?.state,
          zipCode: companySettings.address?.zipCode,
          country: companySettings.address?.country,
          website: companySettings.website,
          phone: companySettings.phone
        },
        regional: regionalSettings,
        notifications: notificationSettings,
        security: securitySettings
      });

      if (settingsResponse.data.success) {
        setUnsavedChanges(false);
        showNotification && showNotification('Settings saved successfully', 'success');
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      logger.error('Error saving settings:', error);
      showNotification && showNotification('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (category, field, value) => {
    setUnsavedChanges(true);
    switch (category) {
      case 'company':
        if (field.includes('.')) {
          const [parent, child] = field.split('.');
          setCompanySettings(prev => ({
            ...prev,
            [parent]: { ...prev[parent], [child]: value }
          }));
        } else {
          setCompanySettings(prev => ({ ...prev, [field]: value }));
        }
        break;
      case 'regional':
        setRegionalSettings(prev => ({ ...prev, [field]: value }));
        break;
      case 'user':
        if (field.includes('.')) {
          const [parent, child] = field.split('.');
          setUserSettings(prev => ({
            ...prev,
            [parent]: { ...prev[parent], [child]: value }
          }));
        } else {
          setUserSettings(prev => ({ ...prev, [field]: value }));
        }
        break;
      case 'notifications':
        setNotificationSettings(prev => ({ ...prev, [field]: value }));
        break;
      case 'security':
        setSecuritySettings(prev => ({ ...prev, [field]: value }));
        break;
      default:
        break;
    }
  };

  const renderCompanySettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <BuildingOfficeIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Business Information</h3>
              <p className="text-sm text-gray-600">Manage your company profile and basic information</p>
            </div>
          </div>
        </CardHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <InputComponent
              label="Business Name"
              placeholder="Enter your business name"
              value={companySettings.businessName}
              onChange={(e) => updateSetting('company', 'businessName', e.target.value)}
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Industry</label>
              <select
                value={companySettings.industry}
                onChange={(e) => updateSetting('company', 'industry', e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
              >
                <option value="">Select Industry</option>
                <option value="technology">Technology</option>
                <option value="healthcare">Healthcare</option>
                <option value="finance">Finance</option>
                <option value="education">Education</option>
                <option value="retail">Retail</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="consulting">Consulting</option>
                <option value="real-estate">Real Estate</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Business Type</label>
              <select
                value={companySettings.businessType}
                onChange={(e) => updateSetting('company', 'businessType', e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
              >
                <option value="corporation">Corporation</option>
                <option value="llc">LLC</option>
                <option value="partnership">Partnership</option>
                <option value="sole-proprietorship">Sole Proprietorship</option>
                <option value="non-profit">Non-Profit</option>
              </select>
            </div>

            <InputComponent
              label="Website"
              placeholder="https://yourcompany.com"
              value={companySettings.website}
              onChange={(e) => updateSetting('company', 'website', e.target.value)}
              type="url"
            />

            <InputComponent
              label="Phone Number"
              placeholder="+1 (555) 123-4567"
              value={companySettings.phone}
              onChange={(e) => updateSetting('company', 'phone', e.target.value)}
              type="tel"
            />
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="font-semibold text-gray-900 dark:text-white">Business Address</h3>
        </CardHeader>

        <div className="space-y-4">
          <div className="lg:col-span-2">
            <InputComponent
              label="Street Address"
              placeholder="123 Business Street"
              value={companySettings.address.street}
              onChange={(e) => updateSetting('company', 'address.street', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <InputComponent
              label="City"
              placeholder="New York"
              value={companySettings.address.city}
              onChange={(e) => updateSetting('company', 'address.city', e.target.value)}
            />

            <InputComponent
              label="State/Province"
              placeholder="NY"
              value={companySettings.address.state}
              onChange={(e) => updateSetting('company', 'address.state', e.target.value)}
            />

            <InputComponent
              label="ZIP/Postal Code"
              placeholder="10001"
              value={companySettings.address.zipCode}
              onChange={(e) => updateSetting('company', 'address.zipCode', e.target.value)}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
              <select
                value={companySettings.address.country}
                onChange={(e) => updateSetting('company', 'address.country', e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
              >
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Australia">Australia</option>
                <option value="Germany">Germany</option>
                <option value="France">France</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderRegionalSettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
              <GlobeAltIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Regional & Format Settings</h3>
              <p className="text-sm text-gray-600">Configure language, timezone, and display formats</p>
            </div>
          </div>
        </CardHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Language</label>
            <select
              value={regionalSettings.language}
              onChange={(e) => updateSetting('regional', 'language', e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
            >
              <option value="en">English (US)</option>
              <option value="en-gb">English (UK)</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="pt">Portuguese</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Timezone</label>
            <select
              value={regionalSettings.timezone}
              onChange={(e) => updateSetting('regional', 'timezone', e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="UTC">UTC (GMT)</option>
              <option value="Europe/London">London (GMT/BST)</option>
              <option value="Europe/Paris">Paris (CET/CEST)</option>
              <option value="Asia/Tokyo">Tokyo (JST)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date Format</label>
            <select
              value={regionalSettings.dateFormat}
              onChange={(e) => updateSetting('regional', 'dateFormat', e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
            >
              <option value="MM/DD/YYYY">MM/DD/YYYY (US)</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY (UK)</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (ISO)</option>
              <option value="DD.MM.YYYY">DD.MM.YYYY (German)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Time Format</label>
            <select
              value={regionalSettings.timeFormat}
              onChange={(e) => updateSetting('regional', 'timeFormat', e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
            >
              <option value="12h">12 Hour (AM/PM)</option>
              <option value="24h">24 Hour</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Currency</label>
            <select
              value={regionalSettings.currency}
              onChange={(e) => updateSetting('regional', 'currency', e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
            >
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="AUD">AUD - Australian Dollar</option>
              <option value="JPY">JPY - Japanese Yen</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Number Format</label>
            <select
              value={regionalSettings.numberFormat}
              onChange={(e) => updateSetting('regional', 'numberFormat', e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
            >
              <option value="US">1,234.56 (US)</option>
              <option value="EU">1.234,56 (European)</option>
              <option value="IN">1,23,456.78 (Indian)</option>
            </select>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <Card className="p-6 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm">
            <InformationCircleIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notification Preferences</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Manage how you receive updates and alerts</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900 dark:text-white">Email Notifications</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Receive important updates via email</p>
            </div>
            <ToggleSwitch
              checked={notificationSettings.emailNotifications}
              onChange={(e) => updateSetting('notifications', 'emailNotifications', e.target.checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900 dark:text-white">System Alerts</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Get notified about system events and changes</p>
            </div>
            <ToggleSwitch
              checked={notificationSettings.systemAlerts}
              onChange={(e) => updateSetting('notifications', 'systemAlerts', e.target.checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900 dark:text-white">Security Alerts</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Critical security notifications and warnings</p>
            </div>
            <ToggleSwitch
              checked={notificationSettings.securityAlerts}
              onChange={(e) => updateSetting('notifications', 'securityAlerts', e.target.checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900 dark:text-white">Weekly Reports</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Receive weekly summary of your business activities</p>
            </div>
            <ToggleSwitch
              checked={notificationSettings.weeklyReports}
              onChange={(e) => updateSetting('notifications', 'weeklyReports', e.target.checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900 dark:text-white">Invoice Reminders</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Payment and invoice due date reminders</p>
            </div>
            <ToggleSwitch
              checked={notificationSettings.invoiceReminders}
              onChange={(e) => updateSetting('notifications', 'invoiceReminders', e.target.checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900 dark:text-white">Marketing Emails</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Product updates and promotional content</p>
            </div>
            <ToggleSwitch
              checked={notificationSettings.marketingEmails}
              onChange={(e) => updateSetting('notifications', 'marketingEmails', e.target.checked)}
            />
          </div>
        </div>
      </Card>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      {/* Password & Security */}
      <Card className="p-6 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-sm">
            <CheckCircleIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Password & Security</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Manage password policies and security settings</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">Password Expiry (days)</label>
            <InputComponent
              type="number"
              min="30"
              max="365"
              value={securitySettings.passwordExpiry}
              onChange={(e) => updateSetting('security', 'passwordExpiry', parseInt(e.target.value) || 90)}
              placeholder="90"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">Force password change after specified days</p>
          </div>

          <div className="p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">IP Whitelist</label>
            <Textarea
              value={securitySettings.ipWhitelist}
              onChange={(e) => updateSetting('security', 'ipWhitelist', e.target.value)}
              placeholder="192.168.1.1&#10;10.0.0.1"
              rows={3}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">Enter IP addresses (one per line) that can access your account</p>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900 dark:text-white">Activity Log</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Track all account activities and login attempts</p>
            </div>
            <ToggleSwitch
              checked={securitySettings.activityLog}
              onChange={(e) => updateSetting('security', 'activityLog', e.target.checked)}
            />
          </div>
        </div>
      </Card>

      {/* Organization Settings */}
      <Card className="p-6 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-sm">
            <BuildingOfficeIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Organization Settings</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Configure data retention and backup policies</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">Data Retention (days)</label>
            <InputComponent
              type="number"
              min="30"
              max="3650"
              value={securitySettings.dataRetention}
              onChange={(e) => updateSetting('security', 'dataRetention', parseInt(e.target.value) || 365)}
              placeholder="365"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">How long to retain deleted data before permanent removal</p>
          </div>

          <div className="p-4 bg-gray-50 dark:bg-[#222222] rounded-lg">
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">Backup Frequency</label>
            <select
              value={securitySettings.backupFrequency}
              onChange={(e) => updateSetting('security', 'backupFrequency', e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">Automated backup schedule</p>
          </div>
        </div>
      </Card>

      {/* Domains */}
      <Card className="p-6 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm">
            <GlobeAltIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Domains</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">All configured domains and their health status</p>
          </div>
        </div>

        <div className="space-y-3">
          {domains.length > 0 ? (
            domains.map((domain, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#222222] rounded-lg hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">{domain.domainName || domain.domain || domain.name}</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {domain.isVerified ? 'Verified' : 'Pending Verification'} • Last checked: {domain.healthLastChecked ? new Date(domain.healthLastChecked).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    domain.status === 'healthy' || domain.status === 'active'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : domain.status === 'warning'
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                  }`}>
                    {domain.status || 'Unknown'}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <GlobeAltIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No domains configured yet</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Add domains in the Domains section</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-300 dark:bg-[#222222] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-500 dark:peer-focus:ring-orange-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed shadow-inner"></div>
    </label>
  );

  const tabs = [
    {
      id: 'company',
      label: 'Company',
      icon: <BuildingOfficeIcon className="w-4 h-4" />,
      description: 'Business information and address'
    },
    {
      id: 'regional',
      label: 'Regional',
      icon: <GlobeAltIcon className="w-4 h-4" />,
      description: 'Language, timezone, and formats'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: <InformationCircleIcon className="w-4 h-4" />,
      description: 'Email and system notifications'
    },
    {
      id: 'security',
      label: 'Security',
      icon: <CheckCircleIcon className="w-4 h-4" />,
      description: 'Security and privacy settings'
    },
    {
      id: 'workspace-search',
      label: 'Workspace Search',
      icon: <MagnifyingGlassIcon className="w-4 h-4" />,
      description: 'AI search sources and usage'
    },
  ];

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800">
        <div className="px-6 pt-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CogIconSolid className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Settings</h1>
              {unsavedChanges && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                  <ExclamationTriangleIcon className="w-3 h-3 text-yellow-600 dark:text-yellow-500" />
                  <span className="text-[11px] font-medium text-yellow-700 dark:text-yellow-400">Unsaved</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchSettings}
                disabled={loading}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-[#222222] hover:bg-gray-200 dark:hover:bg-[#2a2a2a] text-gray-700 dark:text-gray-200 rounded-md text-xs font-medium transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !unsavedChanges}
                className="flex items-center gap-1.5 px-3 py-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 dark:disabled:bg-[#222222] text-white rounded-md text-xs font-medium transition-colors disabled:cursor-not-allowed"
              >
                <CheckCircleIcon className="w-3.5 h-3.5" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-800">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/10'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]/50'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-[#111111]">
        <div className="h-full overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <ArrowPathIcon className="w-6 h-6 text-orange-600 dark:text-orange-400 animate-spin" />
                  </div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Loading settings...</p>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'company' && renderCompanySettings()}
                {activeTab === 'regional' && renderRegionalSettings()}
                {activeTab === 'notifications' && renderNotificationSettings()}
                {activeTab === 'security' && renderSecuritySettings()}
                {/* Saves via its own button (admin-service PUT), not the header Save */}
                {activeTab === 'workspace-search' && <WorkspaceSearchSettings showNotification={showNotification} />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernSettings;